import LegalPage from "./LegalPage";

export default function Privacy() {
  return (
    <LegalPage
      title="Privacy Policy"
      subtitle="How we handle your information"
      lastUpdated="February 14, 2026"
    >
      <p>
        HappyPlate LLC ("HappyPlate," "Plate Buddies," "we," "us") respects your family's privacy. This
        policy explains what we collect, why we collect it, and what choices you have.
      </p>

      <h2>1. Information we collect</h2>
      <h3>Information you give us</h3>
      <ul>
        <li><strong>Order details:</strong> name, shipping address, email, phone (optional). Payment is handled directly by Stripe — we never see your full card number.</li>
        <li><strong>Email signups:</strong> your email address and the source page you signed up from.</li>
        <li><strong>Pilot program applications:</strong> name, email, city, your kids' ages, your description of your mealtime struggle, current habits, why you'd be a fit, and an optional Instagram handle.</li>
        <li><strong>Custom Buddy creations:</strong> the name and prompt your child gives the AI Buddy Generator, plus the resulting illustration. We store these so you can re-print or revisit them later.</li>
        <li><strong>Meal plans:</strong> if you save a weekly plan, we store the email you provide along with the chosen Buddies and dates.</li>
      </ul>
      <h3>Information collected automatically</h3>
      <ul>
        <li><strong>Local browser storage:</strong> we use your browser's local storage to remember your cart, your access entitlements, and the Buddies you've designed. This data lives on your device and is not transmitted to our servers unless you submit a form.</li>
        <li><strong>Standard server logs:</strong> IP address, user agent, and request timestamps for security and debugging.</li>
      </ul>

      <h2>2. How we use your information</h2>
      <ul>
        <li>Fulfill and ship your orders.</li>
        <li>Email you order confirmations, shipping updates, and (if you opt in) product updates.</li>
        <li>Review your pilot program application and contact you about the outcome.</li>
        <li>Generate AI Buddy illustrations using third-party AI APIs.</li>
        <li>Improve the product, fix bugs, and keep the service running safely.</li>
      </ul>

      <h2>3. Children's privacy</h2>
      <p>
        Plate Buddies is designed <em>for families with kids ages 2–7</em>, but our website is intended to
        be used <strong>by parents and guardians</strong>, not directly by children. We do not knowingly
        collect personal information from children under 13. If you believe a child has submitted
        personal information without parental consent, contact us at{" "}
        <a href="mailto:hello@platebuddies.com">hello@platebuddies.com</a> and we'll delete it promptly.
      </p>

      <h2>4. Service providers we share with</h2>
      <p>We rely on a small set of trusted vendors to operate the site:</p>
      <ul>
        <li><strong>Stripe</strong> — payment processing.</li>
        <li><strong>Resend</strong> — transactional email delivery.</li>
        <li><strong>Google (Gemini)</strong> — AI image generation for Custom Buddies.</li>
        <li><strong>Cloud hosting & database providers</strong> — to host the site and store your account data securely.</li>
      </ul>
      <p>We do not sell your personal information.</p>

      <h2>5. Cookies & tracking</h2>
      <p>
        We use first-party browser storage to remember your cart and access entitlements. We do not
        currently set marketing cookies or use cross-site tracking pixels. If we add analytics in the
        future, we'll update this policy and request consent where required.
      </p>

      <h2>6. Your choices</h2>
      <ul>
        <li><strong>Unsubscribe</strong> from marketing emails using the link in any email we send.</li>
        <li><strong>Request a copy or deletion</strong> of your personal information by emailing <a href="mailto:hello@platebuddies.com">hello@platebuddies.com</a>. We respond within 30 days.</li>
        <li><strong>Clear local data</strong> at any time by clearing your browser's site data for platebuddies.com — this removes saved cart items, custom Buddies, and access entitlements stored on your device.</li>
      </ul>

      <h2>7. Data retention</h2>
      <p>
        We keep order records for as long as required by tax and accounting law (typically 7 years). Email
        signups are kept until you unsubscribe. Pilot applications are kept up to 12 months unless you ask
        us to delete them sooner.
      </p>

      <h2>8. Security</h2>
      <p>
        We use industry-standard practices to protect your data, including encrypted connections (HTTPS)
        across the site and encrypted storage with our cloud providers. No system is perfectly secure — if
        a breach occurs, we'll notify affected users without undue delay.
      </p>

      <h2>9. Updates to this policy</h2>
      <p>
        If we make material changes, we'll update the "Last updated" date and (for significant changes)
        email anyone on our list with active accounts.
      </p>

      <h2>10. Contact</h2>
      <p>
        HappyPlate LLC<br />
        Oklahoma, United States<br />
        Email: <a href="mailto:hello@platebuddies.com">hello@platebuddies.com</a>
      </p>
    </LegalPage>
  );
}
